import type { FC } from "react";
import { ProfileSectionHeader } from "./ProfileSectionHeader";
import {
  useCountryId,
  usePhone,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import SpeechBubbleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/SpeechBubbleIconPath";
import LightbulbWithRaysIconPath from "../../../../shared/wrappers/FixaSSRIcon/paths/LightbulbWithRaysIconPath";
import { useTranslation } from "@coworker/locales";
import { useMyTeam } from "@coworker/app/src/core/hooks/useStoreTeams";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import { languageMapper } from "../../../helpers/profileLanguageHelper";
import { useCountries } from "@coworker/app/src/core/hooks/useCountries";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import Support from "@coworker/app/src/components/Profile/Support";
import PhoneNumbers from "@coworker/app/src/components/Profile/AddPhoneNumber";
import Language from "@coworker/app/src/components/Language";
import StoreContacts from "@coworker/app/src/components/StoreContacts";
import { FixaListView } from "../../../../shared/wrappers/FixaListView/FixaListView";
import { ProfileSectionListViewItem } from "./ProfileSectionListViewItem";
import { StoreEnvironment } from "../StoreEnvironment/StoreEnvironment";
import { SimpleHalfSheet } from "../../../../shared/simple/SimpleHalfsheet/SimpleHalfSheet";
import PrivacyStatement from "../PrivacyStatement/PrivacyStatement";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

export const ProfileSections: FC = () => {
  const { t } = useTranslation();
  const phone = usePhone();
  const store = useMyStore();
  const storePrimaryLocale = store?.primaryLocale;
  const [userLanguage] = useUserPreference("language", storePrimaryLocale);
  const myTeam = useMyTeam();
  const isChina = isChineseEnvironment();
  const languageName = languageMapper(userLanguage).name;
  const myCountryId = useCountryId();
  const { data: countries } = useCountries();
  const myCountry = countries?.find(
    (country) => country.isoCode === myCountryId
  );
  // TO-DO: COAPP-12900 - delete enableAtkomst and country row https://jira.digital.ingka.com/browse/COAPP-12900
  const enableAtkomst = useFlag(FLAGS.ENABLE_ATKOMST);

  const version = new Date(document.lastModified).toISOString();

  return (
    <>
      <ProfileSectionHeader titleTranslationKey={"profileString"} />
      <FixaListView id="profile">
        {/* TO-DO: COAPP-12900 - delete country row https://jira.digital.ingka.com/browse/COAPP-12900 */}
        {!enableAtkomst && (
          <StoreEnvironment
            trigger={
              <ProfileSectionListViewItem
                key={"country"}
                id={"country"}
                loading={!isChina && myCountry === undefined}
                isNavigational={true}
                titleTranslationKey={"adminCountryFilter"}
                description={isChina ? "China" : (myCountry?.name ?? "")}
                isTitleBold={true}
              />
            }
          />
        )}

        <StoreEnvironment
          trigger={
            <ProfileSectionListViewItem
              key={"store"}
              id={"store"}
              loading={!store}
              isNavigational={true}
              titleTranslationKey={"adminStoreFilter"}
              description={`${store?.storeId} - ${store?.name}`}
              isTitleBold={true}
            />
          }
        />
        <StoreEnvironment
          trigger={
            <ProfileSectionListViewItem
              key={"team"}
              id={"team"}
              loading={!myTeam}
              isNavigational={true}
              titleTranslationKey={"teamString"}
              description={myTeam?.name ?? ""}
              isTitleBold={true}
            />
          }
        />
        <SimpleHalfSheet
          title={"languageString"}
          trigger={
            <ProfileSectionListViewItem
              key={"updateLanguage"}
              id={"updateLanguage"}
              loading={languageName && !languageName.length}
              isNavigational={true}
              titleTranslationKey={"languageString"}
              description={languageName}
              isTitleBold={true}
            />
          }
        >
          <Language />
        </SimpleHalfSheet>
        <SimpleHalfSheet
          title={"storeString"}
          isFullWidth={true}
          trigger={
            <ProfileSectionListViewItem
              key={"storeContacts"}
              id={"storeContacts"}
              loading={false}
              isNavigational={true}
              titleTranslationKey={"storeString"}
              description={t("findAllStoreContactsString")}
              isTitleBold={true}
            />
          }
        >
          <StoreContacts />
        </SimpleHalfSheet>
        <SimpleHalfSheet
          title={phone ? "updateContactString" : "addContactString"}
          isFullWidth={true}
          trigger={
            <ProfileSectionListViewItem
              key={"storeContacts"}
              id={"storeContacts"}
              loading={false}
              isNavigational={true}
              titleTranslationKey={
                phone ? "updateContactString" : "addContactString"
              }
              description={phone ?? t("noneString")}
              isTitleBold={true}
            />
          }
        >
          <PhoneNumbers />
        </SimpleHalfSheet>
      </FixaListView>
      <ProfileSectionHeader titleTranslationKey={"supportString"} />
      <FixaListView id="help">
        <SimpleHalfSheet
          title={"superUsersString"}
          isFullWidth={true}
          trigger={
            <ProfileSectionListViewItem
              key={"superUsers"}
              id={"superUsers"}
              loading={false}
              isNavigational={true}
              titleTranslationKey={"superUsersString"}
              description={""}
              isTitleBold={false}
            />
          }
        >
          <Support />
        </SimpleHalfSheet>
        <SimpleHalfSheet
          title={"privacyString"}
          trigger={
            <ProfileSectionListViewItem
              key={"privacy"}
              id={"privacy"}
              loading={false}
              isNavigational={true}
              titleTranslationKey={"privacyString"}
              description={""}
              isTitleBold={false}
            />
          }
        >
          <PrivacyStatement />
        </SimpleHalfSheet>
        <a
          href={
            "https://ikea.csod.com/ui/lms-learner-search/search?pageNumber=1&query=fixa"
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          <ProfileSectionListViewItem
            key={"training"}
            id={"training"}
            loading={false}
            isNavigational={true}
            titleTranslationKey={"trainingString"}
            description={""}
            isTitleBold={false}
            ssrIcon={LightbulbWithRaysIconPath}
          />
        </a>
        <a
          href={
            "https://engage.cloud.microsoft/main/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIzMzE5OTkzOTU4NCJ9/all"
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          <ProfileSectionListViewItem
            key={"feedback"}
            id={"feedback"}
            loading={false}
            isNavigational={true}
            titleTranslationKey={"giveFeedbackString"}
            description={""}
            isTitleBold={false}
            ssrIcon={SpeechBubbleIconPath}
          />
        </a>
        <ProfileSectionListViewItem
          key={"version"}
          id={"version"}
          loading={false}
          isNavigational={false}
          titleTranslationKey={"versionString"}
          description={version}
          isTitleBold={true}
        />
      </FixaListView>
    </>
  );
};
